import {
  Component,
  computed,
  inject,
  input,
  OnInit,
  Signal,
  signal,
  InputSignal,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlContainer,
  FormControl,
  FormGroupDirective,
  FormsModule,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { ImageStore } from '../../../../services/api/image/image-store.service';
import { FormInputComponent } from '../form-input.component';
import { AppendTestIdPipe } from '../../../../pipes/append-test-id/append-test-id.pipe';
import { ErrorMessageComponent } from '../../../layout/error-message/error-message.component';
import { fromEvent } from 'rxjs';
import { LoadingAnimationComponent } from '../../../feedback/loading-animation/loading-animation.component';
import { EEntityType, ERelationType, IImage } from '@web/dtos';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';

@Component({
  selector: 'aw-image-select',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AppendTestIdPipe,
    ErrorMessageComponent,
    LoadingAnimationComponent,
    FilterTextsPipe,
    LanguagePipe,
  ],
  templateUrl: './image-select.component.html',
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  styles: ``,
})
export class ImageSelectComponent extends FormInputComponent implements OnInit {
  protected readonly imageStore: ImageStore = inject(ImageStore);

  protected readonly showList: WritableSignal<boolean> = signal(false);
  protected readonly imageSearchControl: FormControl = new FormControl('');
  protected readonly selectedItem: WritableSignal<number | undefined> =
    signal(undefined);
  public readonly images: Signal<IImage[]> = this.imageStore.images;
  public readonly allowedFileExtensions: InputSignal<string[]> = input<
    string[]
  >([]);
  protected readonly availableImages: Signal<IImage[]> = computed(
    (): IImage[] => {
      const allowedExtensions: string[] = this.allowedFileExtensions().map(
        (extension: string): string =>
          extension.replace(/^\./, '').toLocaleLowerCase()
      );
      if (allowedExtensions.length === 0) {
        return this.images();
      }
      return this.images().filter((image: IImage): boolean =>
        allowedExtensions.includes(image.fileExtension.toLocaleLowerCase())
      );
    }
  );
  protected readonly isLoading: Signal<boolean> = this.imageStore.isLoading;
  public filteredImages: IImage[] = [];

  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly ERelationType: typeof ERelationType = ERelationType;
  public readonly usageLabel: InputSignal<string | undefined> = input<
    string | undefined
  >(undefined);

  public readonly persistUsageOnSelect: InputSignal<boolean> = input(false);

  constructor(public override ngControl: NgControl) {
    super(ngControl);
  }

  ngOnInit() {
    this.filteredImages = this.availableImages();
    this.setSearchTermToFilename();
    this.imageSearchControl.updateValueAndValidity();
    this.subscription.add(
      fromEvent<MouseEvent & { target?: { id: string } }>(
        document,
        'click'
      ).subscribe(event => {
        const isCorrectTarget =
          event?.target?.id === this.id() ||
          event?.target?.id === this.id() + '-image-icon';
        if (!isCorrectTarget) {
          this.showList.set(false);
          this.setSearchTermToFilename();
        } else if (isCorrectTarget && !this.showList()) {
          this.filteredImages = this.availableImages();
          this.showList.set(true);
        }
      })
    );
    this.subscription.add(
      fromEvent<KeyboardEvent & { target?: { id: string } }>(
        document,
        'keyup'
      ).subscribe(event => {
        const options: IImage[] = this.availableImages();
        const isCorrectTarget = event.target?.id === this.id();
        if (isCorrectTarget) {
          event.preventDefault();
          if (event.key === 'ArrowDown' && isCorrectTarget) {
            this.selectedItem.set(
              this.selectedItem() !== undefined
                ? this.clampSelectedItem(
                    this.selectedItem()! + 1,
                    options.length
                  )
                : 0
            );
          } else if (event.key === 'ArrowUp' && isCorrectTarget) {
            this.selectedItem.set(
              this.selectedItem() !== 0 && this.selectedItem() !== undefined
                ? this.clampSelectedItem(
                    this.selectedItem()! - 1,
                    options.length
                  )
                : undefined
            );
          } else if (
            event.key === 'Enter' &&
            isCorrectTarget &&
            this.selectedItem() !== undefined &&
            this.showList()
          ) {
            this.selectImage(options?.[this.selectedItem()!]);
          }
        } else if (!isCorrectTarget && this.showList()) {
          event.preventDefault();
          this.showList.set(false);
        }
      })
    );
    this.subscription.add(
      this.ngControl.control!.valueChanges.subscribe(value => {
        this.ngControl.control!.markAsTouched();
      })
    );
    this.subscription.add(
      this.imageSearchControl.valueChanges.subscribe(value => {
        const searchTerm = value.toLowerCase();
        this.filteredImages = this.availableImages().filter((image: IImage) => {
          return image.filename.toLowerCase().includes(searchTerm);
        });
      })
    );
  }

  private setSearchTermToFilename() {
    this.imageSearchControl.setValue(
      this.ngControl.control!.value?.filename ?? ''
    );
  }

  public clearSelection(): void {
    const previous = this.ngControl.control?.value as IImage | null;
    const usageLabel = this.usageLabel();

    if (usageLabel && previous?.id) {
      const updated = this.imageStore
        .images()
        .map(img =>
          img.id === previous.id
            ? { ...img, usage: this.removeLabel(img.usage, usageLabel) }
            : img
        );

      if (this.persistUsageOnSelect()) {
        this.imageStore.save(updated);
      }
    }

    this.ngControl.control?.setValue(undefined);
    this.ngControl.control?.updateValueAndValidity();
    this.setSearchTermToFilename();
    this.showList.set(false);
  }

  private clampSelectedItem(newValue: number, maxLength: number): number {
    if (newValue >= maxLength) {
      return maxLength - 1;
    } else if (newValue < 0) {
      return 0;
    }
    return newValue;
  }
  private addLabel(
    currentUsage: string | null | undefined,
    label: string
  ): string {
    const labels = this.parseLabels(currentUsage);
    labels.add(label);
    return Array.from(labels).sort().join(', ');
  }
  private removeLabel(
    currentUsage: string | null | undefined,
    label: string
  ): string | null {
    const labels = this.parseLabels(currentUsage);
    labels.delete(label);
    return labels.size > 0 ? Array.from(labels).sort().join(', ') : null;
  }

  private parseLabels(currentUsage: string | null | undefined): Set<string> {
    return new Set(
      (currentUsage ?? '')
        .split(',')
        .map(v => v.trim())
        .filter(Boolean)
    );
  }
  public selectImage(option?: IImage): void {
    if (!option) return;

    const previous = this.ngControl.control?.value as IImage | null;
    const next = option;
    const usageLabel = this.usageLabel();

    if (usageLabel) {
      const updated = this.imageStore.images().map(img => {
        if (previous?.id && img.id === previous.id && previous.id !== next.id) {
          return { ...img, usage: this.removeLabel(img.usage, usageLabel) };
        }
        if (next.id && img.id === next.id) {
          return { ...img, usage: this.addLabel(img.usage, usageLabel) };
        }
        return img;
      });

      const nextWithUsage = updated.find(i => i.id === next.id) ?? next;
      this.ngControl.control?.setValue(nextWithUsage);

      if (this.persistUsageOnSelect()) {
        this.imageStore.save(updated);
      }
    } else {
      this.ngControl.control?.setValue(next);
    }

    this.ngControl.control?.updateValueAndValidity();
    this.showList.set(false);
  }
}
